import { FunctionComponent } from 'react'
import { Control, Controller, FieldErrors } from 'react-hook-form'
import Multiselect from '../../../elements/Multiselect'

interface IProps {
  name: string
  control: Control<any>
  errors?: FieldErrors<any>
  label?: string
  placeholder?: string
  required?: boolean
  menuPlacement?: 'auto' | 'bottom' | 'top'
  className?: string
  options: Array<{ value: any; label: string }>
  localizedStringFn: (value: any) => string
  localizedStringInverseFn: (localizedString: string) => any
  noneValue: any // The "none" option value (e.g., CreatorPetsOwner.noPets or ChildAgeRange.noChildren)
}

/**
 * MultiSelectWithExclusion
 *
 * Multiselect component with mutual exclusion logic:
 * - If you select the "none" option (e.g., "No pets", "No children"),
 *   all other options are automatically deselected
 * - If you select any other option, the "none" option is automatically deselected
 * - This prevents contradictory selections like "No pets" + "Dog" at the same time
 */
const MultiSelectWithExclusion: FunctionComponent<IProps> = ({
  name,
  control,
  errors,
  label,
  placeholder,
  required = false,
  menuPlacement = 'auto',
  className = '',
  options,
  localizedStringFn,
  localizedStringInverseFn,
  noneValue,
}) => {
  return (
    <div className={`w-full ${className}`}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: required ? 'Please select at least one option.' : false,
        }}
        render={({ field }) => {
          const selectOptions = options.map((option) => ({
            value: option.value,
            label: localizedStringFn(option.value),
          }))

          const selectedOptions =
            field.value?.map((localizedString) => {
              const enumValue = localizedStringInverseFn(localizedString)
              return {
                value: enumValue,
                label: localizedStringFn(enumValue),
              }
            }) || []

          const handleChange = (selected) => {
            if (!selected || selected.length === 0) {
              field.onChange([])
              return
            }

            const hasNoneOption = selected.some(
              (option) => option.value === noneValue,
            )
            const hasOtherOptions = selected.some(
              (option) => option.value !== noneValue,
            )

            // Handle mutually exclusive selections (none option vs having options)
            if (hasNoneOption && hasOtherOptions) {
              const lastSelection = selected[selected.length - 1]
              if (lastSelection.value === noneValue) {
                field.onChange([localizedStringFn(noneValue)])
              } else {
                // Keep only non-"none" options
                field.onChange(
                  selected
                    .filter((option) => option.value !== noneValue)
                    .map((option) => localizedStringFn(option.value)),
                )
              }
            } else {
              field.onChange(
                selected.map((option) => localizedStringFn(option.value)),
              )
            }
          }

          return (
            <>
              {label && (
                <label className='block text-sm leading-5 font-medium text-gray-700'>
                  {label}
                </label>
              )}
              <div className='mt-1'>
                <Multiselect
                  options={selectOptions}
                  selected={selectedOptions}
                  onChange={handleChange}
                  placeholder={placeholder}
                  menuPlacement={menuPlacement}
                />
              </div>
              {errors?.[name]?.message && (
                <p className='mt-2 text-sm text-red-600'>
                  {errors[name].message as string}
                </p>
              )}
            </>
          )
        }}
      />
    </div>
  )
}

export { MultiSelectWithExclusion }
